import React from "react";

import {
  DefenderDokumentStatus,
  DefenderUploadedDokument,
  emptyDefenderUploadedDokument,
  emptyFachkraft,
  Fachkraft,
  FileNotCheckedErrorMessage,
  VirusFileErrorMessage,
} from "interfaces/Application";
import { formMethod } from "interfaces/TableListProps";
import { Formik, Form as FormikForm, Field, useFormikContext, FormikHelpers } from "formik";
import { Form, Alert, Button, Col, Modal, Row } from "react-bootstrap";
import AlertTriangle from "components/AlertTriangle";
import * as yup from "yup";
import { FormikCheck, FormikControl, FormikDateField } from "@gelsenwasser/react";
import { useLicensesQueries } from "../licenses/licensesQueries";
import { useDokumentStatusQueries, useSkillsQueries } from "../applications/applicationsQueries";
import { determineOpeningPage } from "../../interfaces/Pages";
import { FileError } from "../../components/FileError";
import { FileType, useUploadDokumente } from "domain/applications/applicationMutations";
import { log } from "common/Logger";
import FileNameLabel from "components/FileNameLabel";

interface Props {
  installationsUnternehmenId: string;
  fachkraft: Fachkraft;
  alteFachkraft?: Fachkraft;
  //todo: maybe decide based on fachkraft.status, if this is set from outside!
  formMethod?: formMethod;
  enableSparteSelection?: boolean;
  onClose: (isCancel: boolean, fachkraft?: Fachkraft) => void;
}

const formModeSelector = (
  formMethod: formMethod,
  oldEmployee: Fachkraft,
  enableSparteSelection: boolean,
  installationsUnternehmenId: string
) => {
  switch (formMethod) {
    case "create":
    case "edit":
      return (
        <CreateUpdateForm
          formMethod={formMethod}
          enableSparteSelection={enableSparteSelection}
          installationsUnternehmenId={installationsUnternehmenId}
        />
      );

    case "delete":
      return <DeleteForm />;

    case "replace":
      return <ReplaceForm fachkraft={oldEmployee} installationsUnternehmenId={installationsUnternehmenId} />;
  }
};

const checkFileStatus = (
  fileStatus: DefenderUploadedDokument[] | undefined,
  setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void,
  values: Fachkraft
) => {
  if (fileStatus) {
    const befaehigungDateiStatus = fileStatus.find(
      (dud) => dud.documentId === values.befaehigungDatei?.documentId
    )?.status;
    if (
      befaehigungDateiStatus !== DefenderDokumentStatus.NotChecked &&
      values.befaehigungDatei?.status === DefenderDokumentStatus.NotChecked
    ) {
      setFieldValue("befaehigungDatei", {
        ...values.befaehigungDatei,
        status: befaehigungDateiStatus,
      });
    }
    const sicherheitsDateiStatus = fileStatus.find(
      (dud) => dud.documentId === values.sicherheitsDatei?.documentId
    )?.status;
    if (
      sicherheitsDateiStatus !== DefenderDokumentStatus.NotChecked &&
      values.sicherheitsDatei?.status === DefenderDokumentStatus.NotChecked
    ) {
      setFieldValue("sicherheitsDatei", {
        ...values.sicherheitsDatei,
        status: sicherheitsDateiStatus,
      });
    }
    const tgriDateiStatus = fileStatus.find((dud) => dud.documentId === values.tgriDatei?.documentId)?.status;
    if (
      tgriDateiStatus !== DefenderDokumentStatus.NotChecked &&
      values.tgriDatei?.status === DefenderDokumentStatus.NotChecked
    ) {
      setFieldValue("tgriDatei", {
        ...values.tgriDatei,
        status: tgriDateiStatus,
      });
    }
    const sonstigeDateiStatus = fileStatus.find((dud) => dud.documentId === values.sonstigeDatei?.documentId)?.status;
    if (
      sonstigeDateiStatus !== DefenderDokumentStatus.NotChecked &&
      values.sonstigeDatei?.status === DefenderDokumentStatus.NotChecked
    ) {
      setFieldValue("sonstigeDatei", {
        ...values.sonstigeDatei,
        status: sonstigeDateiStatus,
      });
    }
  }
};

const fileHasErrors = (
  file: DefenderUploadedDokument | undefined,
  actions: FormikHelpers<Fachkraft>,
  fieldName: string
) => {
  if (file && file.status === DefenderDokumentStatus.NotChecked) {
    actions.setFieldError(fieldName, FileNotCheckedErrorMessage);
    return true;
  }
  if (file && file.status === DefenderDokumentStatus.VirusFound) {
    actions.setFieldError(fieldName, VirusFileErrorMessage);
    return true;
  }
  return false;
};

const EmployeeBasicForm: React.VFC<Props> = ({
  fachkraft,
  alteFachkraft = emptyFachkraft,
  onClose,
  formMethod = "create",
  enableSparteSelection = false,
  installationsUnternehmenId,
}: Props) => {
  // hooks
  // states
  // parameter
  // queries und mutationen
  // effekte
  // daten
  const formBody = formModeSelector(formMethod, alteFachkraft, enableSparteSelection, installationsUnternehmenId);
  const saveButtonCaption = formMethod !== "delete" ? "Speichern" : "Löschen";

  const formSchema = yup.object().shape({
    vorname: yup.string().max(100, "Nicht mehr als 100 Zeichen").required("Vorname ist notwendig!"),
    nachname: yup.string().max(100, "Nicht mehr als 100 Zeichen").required("Nachname ist notwendig!"),
  });

  const handleSubmit = (values: Fachkraft, actions: FormikHelpers<Fachkraft>) => {
    let hasErrors = false;
    if (!installationsUnternehmenId || installationsUnternehmenId === "") {
      hasErrors = true;
      actions.setFieldError("befaehigungDatei", "Das Unternehmen konnte leider nicht zugordnet werden!");
    }
    if (!values.befaehigungDatei && !values.id) {
      hasErrors = true;
      actions.setFieldError("befaehigungDatei", "Bitte laden Sie eine Datei hoch!");
    }
    if (fileHasErrors(values.befaehigungDatei, actions, "befaehigungDatei")) {
      hasErrors = true;
    }
    if (fileHasErrors(values.sicherheitsDatei, actions, "sicherheitsDatei")) {
      hasErrors = true;
    }
    if (fileHasErrors(values.sonstigeDatei, actions, "sonstigeDatei")) {
      hasErrors = true;
    }
    if (fileHasErrors(values.tgriDatei, actions, "tgriDatei")) {
      hasErrors = true;
    }
    if (!hasErrors) {
      onClose(false, values);
    }
  };

  return (
    <>
      <Formik initialValues={fachkraft} onSubmit={handleSubmit} validationSchema={formSchema}>
        {({ handleSubmit, values, setFieldValue }) => {
          const { data: fileStatus } = useDokumentStatusQueries(
            [
              values.befaehigungDatei ?? emptyDefenderUploadedDokument,
              values.sicherheitsDatei ?? emptyDefenderUploadedDokument,
              values.tgriDatei ?? emptyDefenderUploadedDokument,
              values.sonstigeDatei ?? emptyDefenderUploadedDokument,
            ],
            installationsUnternehmenId
          );

          React.useEffect(() => {
            checkFileStatus(fileStatus, setFieldValue, values);
          }, [fileStatus?.map((d) => d.documentId + d.status).join(",")]);

          return (
            <FormikForm onSubmit={handleSubmit}>
              <Modal.Body>
                {formBody}
                <Row>
                  <Col>
                    <Alert variant="info">
                      <AlertTriangle color="#37AFB9" />
                      <p>
                        Voraussetzungen zur Eintragung für Fachkräfte. Dokumente als einzelne Dokumente hochladen und
                        nicht als Zip Datei:
                      </p>
                      <p>
                        <ol>
                          <li>Meisterprüfung</li>
                          <li>Sicherheits - und Instandhaltungstechnik (Prüfung + Lehrgang)</li>
                          <li>aktueller TGRI-Lehrgang</li>
                          <li>Sonstige Dokumente</li>
                        </ol>
                      </p>
                      <p>Pro Punkt kann eine Datei hochgeladen werden</p>
                      <p>
                        Die Verarbeitung personenbezogener Daten erfolgt nur zum Zweck der Bearbeitung Ihres Anliegens.
                        Weitere Informationen zum Thema Datenschutz finden Sie auf unserer{" "}
                        <a rel="noreferrer" href={determineOpeningPage(window.location.hostname)} target="_blank">
                          Internetseite
                        </a>
                        .
                      </p>
                      <p>
                        Bei Fragen zur Bearbeitung der Daten wenden Sie sich bitte an den zuständigen Ansprechpartner
                        auf der Startseite.
                      </p>
                    </Alert>
                  </Col>
                </Row>
              </Modal.Body>
              <Modal.Footer>
                <Row>
                  <Col>
                    <Button className="mr-3" as="button" type="submit">
                      {saveButtonCaption}
                    </Button>
                    <Button onClick={() => onClose(true)} variant="secondary">
                      Abbrechen
                    </Button>
                  </Col>
                </Row>
              </Modal.Footer>
            </FormikForm>
          );
        }}
      </Formik>
    </>
  );
};

export default EmployeeBasicForm;

const ReplaceForm: React.VFC<Pick<Props, "fachkraft" | "installationsUnternehmenId">> = ({
  fachkraft,
  installationsUnternehmenId,
}: Pick<Props, "fachkraft" | "installationsUnternehmenId">) => {
  return (
    <>
      <Row>
        <Col>
          <span>Alte Fachkraft: {`${fachkraft?.nachname}, ${fachkraft?.vorname}`}</span>
        </Col>
      </Row>
      <Row>
        <Col>
          <FormikControl name="vorname" label="Vorname Fachkraft" />
        </Col>
      </Row>
      <Row>
        <Col>
          <FormikControl name="nachname" label="Nachname Fachkraft" />
        </Col>
      </Row>
      <Row>
        <Col>
          <FormikControl
            name="entferntAm"
            label="Datum des Wechsels"
            placeholder="Datum"
            type="date"
            as={Field}
            component={FormikDateField}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <FormikControl name="email" label="E-Mail" />
        </Col>
      </Row>
      <CreateFormPart />

      <FileUploads installationsUnternehmenId={installationsUnternehmenId} />
    </>
  );
};

type FileUploadProps = {
  installationsUnternehmenId: string;
};

const FileUploads: React.VFC<FileUploadProps> = ({ installationsUnternehmenId }) => {
  const { setFieldValue, values } = useFormikContext<Fachkraft>();
  const { mutate: uploadDokument } = useUploadDokumente();

  const handleFileUpload = (file: File, subject: string, logItem: string, fieldValueName: string) => {
    uploadDokument(
      { datei: file, subject: subject, installationsUnternehmenId: installationsUnternehmenId },
      {
        onSuccess: (data) => {
          log.debug({ obj: data }, `${logItem} hochgeladen`);
          const dud: DefenderUploadedDokument = {
            documentId: data.documentId,
            dateiName: file.name,
            subject: data.subject,
            status: DefenderDokumentStatus.NotChecked,
          };
          setFieldValue(fieldValueName, dud);
        },
        onError: (err) => {
          log.error({ obj: err }, `Fehler beim Hochladen des/r ${logItem}`);
        },
      }
    );
  };

  const handleMeisterbriefFileSelection: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    if (e.currentTarget.files && e.currentTarget.files.length > 0) {
      const file = e.currentTarget.files[0];
      const subject = `${values.vorname} ${values.nachname}:${FileType.Meisterbrief}`;
      const logItem = "Meisterbrief";
      const fieldValueName = "befaehigungDatei";
      handleFileUpload(file, subject, logItem, fieldValueName);
    }
  };

  const handleSicherheitsFileSelection: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    if (e.currentTarget.files && e.currentTarget.files.length > 0) {
      const file = e.currentTarget.files[0];
      const subject = `${values.vorname} ${values.nachname}:${FileType.Sicherheit}`;
      const logItem = "Sicherheitsdatei";
      const fieldValueName = "sicherheitsDatei";
      handleFileUpload(file, subject, logItem, fieldValueName);
    }
  };

  const handleTrgiFileSelection: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    if (e.currentTarget.files && e.currentTarget.files.length > 0) {
      const file = e.currentTarget.files[0];
      const subject = `${values.vorname} ${values.nachname}:${FileType.TGRI}`;
      const logItem = "TGRI-Datei";
      const fieldValueName = "tgriDatei";
      handleFileUpload(file, subject, logItem, fieldValueName);
    }
  };

  const handleSonstigeFileSelection: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    if (e.currentTarget.files && e.currentTarget.files.length > 0) {
      const file = e.currentTarget.files[0];
      const subject = `${values.vorname} ${values.nachname}:${FileType.Sonstige}`;
      const logItem = "Sonstige Datei";
      const fieldValueName = "sonstigeDatei";
      handleFileUpload(file, subject, logItem, fieldValueName);
    }
  };

  return (
    <>
      <Row>
        <Col>
          <Form.File label="Meisterbrief" onChange={handleMeisterbriefFileSelection} data-browse="Datei auswählen" />
          <FileError errorFieldName="befaehigungDatei" />
        </Col>
      </Row>
      <FileNameLabel defenderUploadedDokument={values.befaehigungDatei} />
      <Row>
        <Col>
          <Form.File
            label="Sicherheits - und Instandhaltungstechnik (Prüfung + Lehrgang)"
            onChange={handleSicherheitsFileSelection}
            data-browse="Datei auswählen"
          />
          <FileError errorFieldName="sicherheitsDatei" />
        </Col>
      </Row>
      <FileNameLabel defenderUploadedDokument={values.sicherheitsDatei} />
      <Row>
        <Col>
          <Form.File label="aktueller TRGI-Lehrgang" onChange={handleTrgiFileSelection} data-browse="Datei auswählen" />
          <FileError errorFieldName="tgriDatei" />
        </Col>
      </Row>
      <FileNameLabel defenderUploadedDokument={values.tgriDatei} />
      <Row>
        <Col>
          <Form.File label="Sonstige Dokumente" onChange={handleSonstigeFileSelection} data-browse="Datei auswählen" />
          <FileError errorFieldName="sonstigeDatei" />
        </Col>
      </Row>
      <FileNameLabel defenderUploadedDokument={values.sonstigeDatei} />
    </>
  );
};

const CreateUpdateForm: React.FC<
  Pick<Props, "formMethod" | "enableSparteSelection" | "installationsUnternehmenId">
> = ({ formMethod, enableSparteSelection, installationsUnternehmenId }) => {
  const meineKonzessionen = useLicensesQueries();
  const { setFieldValue } = useFormikContext<Fachkraft>();

  React.useEffect(() => {
    if (meineKonzessionen.data && meineKonzessionen.data.length > 0 && !meineKonzessionen.isLoading) {
      setFieldValue("sparte", meineKonzessionen.data[0].sparte);
    }
  }, [meineKonzessionen.data?.map((k) => k.sparte).join(","), meineKonzessionen.isLoading]);

  return (
    <>
      {formMethod === "create" && enableSparteSelection && (
        <Row>
          <Col>
            <FormikControl label="Sparte" name="sparte" as="select">
              {meineKonzessionen.data?.map((konzession) => (
                <option key={`sparte_${konzession.sparte}`} value={konzession.sparte}>
                  {konzession.sparte}
                </option>
              ))}
            </FormikControl>
          </Col>
        </Row>
      )}
      <Row>
        <Col>
          <FormikControl name="vorname" label="Vorname Fachkraft" />
        </Col>
      </Row>
      <Row>
        <Col>
          <FormikControl name="nachname" label="Nachname Fachkraft" />
        </Col>
      </Row>
      <Row>
        <Col>
          <FormikControl name="email" label="E-Mail" />
        </Col>
      </Row>
      {formMethod === "create" || formMethod === "replace" ? <CreateFormPart /> : <UpdateFormPart />}
      {formMethod !== "delete" && <FileUploads installationsUnternehmenId={installationsUnternehmenId} />}
    </>
  );
};

const CreateFormPart: React.VFC = () => {
  const { values } = useFormikContext<Fachkraft>();
  return (
    <>
      <Row>
        <Col>Die verantwortliche Fachkraft besitzt</Col>
      </Row>
      <Row>
        <Col>
          <FormikCheck
            name="hatMeisterbrief"
            value={values.hatMeisterbrief.toString()}
            label={
              <div>
                die Befähigungen nach den{" "}
                <a
                  href="https://www.gw-energienetze.de/fileadmin/pdf/service/installateurverzeichnis/richtlinie_gwn_installateurverzeichnis_2019.pdf"
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  Richtlinien
                </a>{" "}
                Paragraph 5
              </div>
            }
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <FormikCheck
            name="hatAndereQualifikation"
            value={values.hatAndereQualifikation.toString()}
            label="andere Qualifikation"
          />
        </Col>
      </Row>
      <Row>
        <Col>
          {values.hatAndereQualifikation && (
            <FormikControl as="textarea" name="beschreibungQualifikation" label="Beschreibung" />
          )}
        </Col>
      </Row>
    </>
  );
};

const UpdateFormPart: React.VFC = () => {
  const { data: skills } = useSkillsQueries();

  return (
    <>
      <Row>
        <Col>Fortbildungen zum Erhalt der Befähigung</Col>
      </Row>
      <Row>
        <Col>
          <FormikControl as="select" name="befaehigungId">
            {skills?.map((s) => (
              <option key={`skill_${s.befaehigungsnummer}`} value={s.id}>
                {s.befaehigungstext}
              </option>
            ))}
          </FormikControl>
        </Col>
        <Col>
          <FormikControl
            name="erlangungbefaehigung"
            placeholder="Datum"
            type="date"
            as={Field}
            component={FormikDateField}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <FormikControl
            name="entferntAm"
            label="Datum Entfernung"
            placeholder="Datum"
            type="date"
            as={Field}
            component={FormikDateField}
          />
        </Col>
        <Col />
      </Row>
    </>
  );
};

const DeleteForm: React.VFC = () => {
  return (
    <>
      <Row>
        <Col>
          <h6>Möchten Sie die folgende Fachkraft wirklich löschen?</h6>
        </Col>
      </Row>
      <Row>
        <Col>
          <FormikControl name="vorname" label="Vorname Fachkraft" disabled />
        </Col>
      </Row>
      <Row>
        <Col>
          <FormikControl name="nachname" label="Nachname Fachkraft" disabled />
        </Col>
      </Row>
      <Row>
        <Col>
          <FormikControl name="email" label="E-Mail" disabled />
        </Col>
      </Row>
    </>
  );
};
